import React, { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { DayPicker, Matcher } from "react-day-picker";
import { format, parse, setHours, setMinutes } from "date-fns";
import { useTranslation } from "react-i18next";

type DateTimePickerFieldProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabledSelectDate?: Matcher | Matcher[];
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  name?: string;
  showClear?: boolean; // show an inline clear icon to reset value
  iconCalendar?: React.ReactNode;
  rightAdornment?: React.ReactNode;
};

/**
 * A lightweight date picker field styled to match SelectField.
 * It wraps a native input[type="date"] for broad browser support and simplicity.
 */
const DateTimePickerField: React.FC<DateTimePickerFieldProps> = ({
  label,
  value,
  onChange,
  disabledSelectDate,
  placeholder,
  disabled,
  errorMessage,
  className,
  name,
  showClear,
  iconCalendar,
  rightAdornment,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<string>("");

  useEffect(() => {
    if (value) {
      setSelectedDateTime(value);
    }
  }, [value]);

  // useEffect(() => {
  //   if (!isOpen) return;
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       dropdownRef.current &&
  //       !dropdownRef.current.contains(event.target as Node) &&
  //       containerRef.current &&
  //       !containerRef.current.contains(event.target as Node)
  //     ) {
  //       setSelectedDateTime(value);
  //       setIsOpen(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, [isOpen]);

  const getDropdownStyle = () => {
    if (!containerRef.current || !isOpen) return {} as React.CSSProperties;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const dropdownHeight = 356;
    const margin = 12;
    let left = rect.left;
    let top = rect.bottom + 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + dropdownWidth + margin > vw) left = vw - dropdownWidth - margin;
    if (top + dropdownHeight + margin > vh) top = rect.top - dropdownHeight - 8;
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    return { position: "fixed" as const, left, top, zIndex: 1000 };
  };

  const handleSelect = (d?: Date) => {
    if (!d || disabled) return;
    try {
      const [hours, minutes] = (
        selectedDateTime
          ? format(parse(selectedDateTime, "hh:mm aa, yyyy-MM-dd", new Date()), "HH:mm")
          : "00:00"
      )
        .split(":")
        .map((str) => parseInt(str, 10));
      const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes);
      const formatted = format(newDate, "hh:mm aa, yyyy-MM-dd");
      setSelectedDateTime(formatted);
    } catch {}
  };

  const handleTimeChange = (e) => {
    const time = e.target.value;
    if (!time) return;
    if (!selectedDateTime) {
      return;
    }
    const [hours, minutes] = time.split(":").map((str) => parseInt(str, 10));
    const newSelectedDate = setHours(
      setMinutes(parse(selectedDateTime, "hh:mm aa, yyyy-MM-dd", new Date()), minutes),
      hours
    );
    const formatted = format(newSelectedDate, "hh:mm aa, yyyy-MM-dd");
    setSelectedDateTime(formatted);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setSelectedDateTime("");
    if (onChange) onChange("");
  };

  return (
    <div className={`w-full ${className || ""}`}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
      ) : null}
      <div ref={containerRef}>
        <div className="flex items-center">
          {rightAdornment ? rightAdornment : null}
          <div className="relative">
            <input
              type="text"
              name={name}
              value={selectedDateTime}
              onChange={() => {}}
              disabled={disabled}
              placeholder={placeholder || t("Select date")}
              className={`w-full pr-5 px-3 py-2 border border-gray-200 ${
                rightAdornment ? "rounded-r-lg" : "rounded-lg"
              } text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-500`}
              // onClick={() => !disabled && setIsOpen((o) => !o)}
              onClick={() => !disabled && setIsOpen(true)}
              readOnly
            />
            {showClear && !!selectedDateTime && !disabled ? (
              <button
                type="button"
                aria-label={t("Clear date")}
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <XIcon className="w-4 h-4" />
              </button>
            ) : (
              <>
                {iconCalendar ? (
                  iconCalendar
                ) : (
                  <CalendarIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                )}
              </>
            )}
            {isOpen && (
              <div
                ref={dropdownRef}
                style={getDropdownStyle()}
                className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
              >
                <DayPicker
                  mode="single"
                  selected={
                    selectedDateTime
                      ? parse(selectedDateTime, "hh:mm aa, yyyy-MM-dd", new Date())
                      : undefined
                  }
                  onSelect={handleSelect}
                  showOutsideDays
                  fixedWeeks
                  disabled={disabledSelectDate}
                  className="p-2"
                />
                <div className="p-2 mb-2 text-center">
                  <input
                    type="time"
                    value={
                      selectedDateTime
                        ? format(
                            parse(selectedDateTime, "hh:mm aa, yyyy-MM-dd", new Date()),
                            "HH:mm"
                          )
                        : "00:00"
                    }
                    onChange={handleTimeChange}
                  />
                </div>
                <div className="p-2 flex items-center justify-end gap-2">
                  <button
                    className="px-3 py-1 rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                    onClick={() => {
                      setSelectedDateTime(value);
                      setIsOpen(false);
                    }}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    className="px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => {
                      setIsOpen(false);
                      if (onChange) onChange(selectedDateTime);
                    }}
                  >
                    {t("Save")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {errorMessage ? <p className="mt-1 text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
};

export default DateTimePickerField;
